import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ShoppingCart, Music, Disc, Shirt, Package, X, Plus, Minus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';

export default function ShopPage() {
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const navigate = useNavigate();

  const products = [
    {
      id: 1,
      name: 'Vintage Ave Records Tee',
      price: 40.00,
      category: 'apparel',
      image: 'https://customer-assets.emergentagent.com/job_indie-label-hub-2/artifacts/4c40nbw2_IMG_1082.jpeg',
      description: 'Classic cream tee with orange gramophone logo',
      colors: ['Cream', 'Burnt Orange', 'Black']
    },
    {
      id: 2,
      name: 'Soul Sounds Hoodie',
      price: 65.00,
      category: 'apparel',
      image: 'https://customer-assets.emergentagent.com/job_indie-label-hub-2/artifacts/mq9florr_0D7BD004-.jpeg',
      description: 'Premium cream pullover hoodie with vinyl record logo - "The Future of Vintage Soul"',
      colors: ['Cream', 'Black', 'Maroon']
    },
    {
      id: 3,
      name: 'Vintage Ave Snapback',
      price: 50.00,
      category: 'accessories',
      image: 'https://customer-assets.emergentagent.com/job_indie-label-hub-2/artifacts/184k8wyl_IMG_1235.jpeg',
      description: 'Orange snapback cap with embroidered gramophone logo',
      colors: ['Orange', 'Black', 'Navy']
    },
    {
      id: 4,
      name: "Collector's Edition 45 Vintage Ave Record",
      price: 45.00,
      category: 'vinyl',
      image: 'https://customer-assets.emergentagent.com/job_indie-label-hub-2/artifacts/gn5cyfhc_IMG_8177.jpeg',
      description: 'Limited edition orange 45 RPM vinyl with Vintage Ave Records logo',
      colors: ['Orange Vinyl']
    }
  ];

  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { value: 'all', label: 'All Products', icon: Package },
    { value: 'apparel', label: 'Apparel', icon: Shirt },
    { value: 'vinyl', label: 'Vinyl Accessories', icon: Disc },
    { value: 'accessories', label: 'Accessories', icon: ShoppingCart },
    { value: 'art', label: 'Art & Prints', icon: Music }
  ];

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  const addToCart = (product) => {
    const existingIndex = cart.findIndex(item => item.id === product.id);
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity = (newCart[existingIndex].quantity || 1) + 1;
      setCart(newCart);
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    toast.success(`${product.name} added to cart!`);
    setCartOpen(true);
  };

  const updateQuantity = (itemId, newQty) => {
    if (newQty < 1) {
      removeFromCart(itemId);
      return;
    }
    const newCart = cart.map(item => 
      item.id === itemId ? { ...item, quantity: newQty } : item
    );
    setCart(newCart);
  };

  const removeFromCart = (itemId) => {
    const newCart = cart.filter(item => item.id !== itemId);
    setCart(newCart);
    toast.success('Item removed from cart');
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.price * (item.quantity || 1), 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + (item.quantity || 1), 0);
  };

  const proceedToCheckout = () => {
    setCartOpen(false);
    navigate('/checkout', { state: { cart } });
  };

  return (
    <div className="min-h-screen py-32 bg-[#1A1412]" data-testid="shop-page">
      {/* Floating Cart Button */}
      <button
        onClick={() => setCartOpen(true)}
        className="fixed bottom-6 right-6 z-40 bg-[#D97736] hover:bg-[#F08F4D] text-white p-4 rounded-full shadow-2xl transition-all"
        data-testid="cart-button"
      >
        <ShoppingCart size={24} />
        {cart.length > 0 && (
          <span className="absolute -top-2 -right-2 bg-[#CFB53B] text-[#1A1412] w-6 h-6 rounded-full text-sm font-bold flex items-center justify-center">
            {getCartCount()}
          </span>
        )}
      </button>

      {/* Cart Sidebar */}
      <AnimatePresence>
        {cartOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-50"
              onClick={() => setCartOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-[#2C221F] z-50 shadow-2xl"
            >
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-[#3D302C]">
                  <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#FDFBF7]">
                    Your Cart ({getCartCount()})
                  </h2>
                  <button 
                    onClick={() => setCartOpen(false)}
                    className="p-2 hover:bg-[#3D302C] rounded-lg"
                  >
                    <X className="text-[#A89B95]" size={24} />
                  </button>
                </div>

                {/* Cart Items */}
                <div className="flex-1 overflow-y-auto p-6">
                  {cart.length === 0 ? (
                    <div className="text-center py-12">
                      <ShoppingCart className="w-16 h-16 text-[#3D302C] mx-auto mb-4" />
                      <p className="text-[#A89B95]">Your cart is empty</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {cart.map((item) => (
                        <div key={item.id || `${item.name}-${item.price}`} className="flex gap-4 p-4 bg-[#1A1412] rounded-xl">
                          <img 
                            src={item.image} 
                            alt={item.name} 
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                          <div className="flex-1">
                            <h3 className="text-[#FDFBF7] font-semibold text-sm">{item.name}</h3>
                            <p className="text-[#D97736] font-bold">${item.price.toFixed(2)}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <button 
                                onClick={() => updateQuantity(item.id, (item.quantity || 1) - 1)}
                                className="w-7 h-7 bg-[#3D302C] rounded flex items-center justify-center text-[#FDFBF7] hover:bg-[#4D403C]"
                              >
                                <Minus size={14} />
                              </button>
                              <span className="text-[#FDFBF7] w-6 text-center">{item.quantity || 1}</span>
                              <button 
                                onClick={() => updateQuantity(item.id, (item.quantity || 1) + 1)}
                                className="w-7 h-7 bg-[#3D302C] rounded flex items-center justify-center text-[#FDFBF7] hover:bg-[#4D403C]"
                              >
                                <Plus size={14} />
                              </button>
                              <button 
                                onClick={() => removeFromCart(item.id)}
                                className="ml-auto p-1 text-red-500 hover:bg-red-500/10 rounded"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer */}
                {cart.length > 0 && (
                  <div className="p-6 border-t border-[#3D302C]">
                    <div className="flex justify-between mb-4">
                      <span className="text-[#A89B95]">Subtotal:</span>
                      <span className="text-[#CFB53B] text-xl font-bold">${getCartTotal().toFixed(2)}</span>
                    </div>
                    <p className="text-[#A89B95] text-sm mb-4 text-center">
                      💵 CashApp: <span className="text-[#00D632] font-bold">$vintageavemusic</span> | Venmo: <span className="text-[#008CFF] font-bold">@VINTAGEAVERECORDS</span>
                    </p>
                    <Button 
                      onClick={proceedToCheckout}
                      className="w-full bg-[#D97736] hover:bg-[#F08F4D] text-white py-6 text-lg"
                    >
                      Checkout
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Video Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="rounded-2xl overflow-hidden shadow-2xl bg-[#2C221F]">
            <video 
              autoPlay 
              loop 
              muted 
              playsInline
              controls
              className="w-full h-auto max-h-[500px] object-cover"
            >
              <source src="https://customer-assets.emergentagent.com/job_indie-label-hub-2/artifacts/y1hhvowk_The%20Future%20of%20Vintage%20Ave.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </motion.div>

        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-[#CFB53B] uppercase tracking-[0.2em] text-xs font-['Outfit'] font-light">Official Merchandise</span>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#FDFBF7] mb-4 tracking-tight mt-4">
            Vintage Ave Shop
          </h1>
          <p className="text-[#A89B95] text-lg font-['Outfit'] max-w-2xl mx-auto">
            Rep the label with exclusive merch, vinyl accessories, and collectibles
          </p>
        </div>

        {/* Limited Edition Pre-Order Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="bg-gradient-to-br from-[#D97736] via-[#E8A54B] to-[#CFB53B] rounded-2xl p-10 relative overflow-hidden text-center">
            {/* Content */}
            <div className="relative z-10">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 italic drop-shadow-lg">
                Limited Edition Label Sampler
              </h2>
              <p className="text-white/90 font-['Outfit'] text-lg mb-6 max-w-2xl mx-auto italic">
                Get our exclusive compilation vinyl featuring tracks from all roster artists. Only 500 pressed!
              </p>
              <Link to="/waitlist">
                <Button className="bg-white hover:bg-[#FDFBF7] text-[#D97736] rounded-2xl px-10 py-4 text-lg font-semibold shadow-lg">
                  Pre-Order Now
                </Button>
              </Link>
              <p className="text-white/80 text-sm mt-4 font-['Outfit']">
                💵 Accepting CashApp: <span className="font-bold">$vintageavemusic</span> | Venmo: <span className="font-bold">@VINTAGEAVERECORDS</span>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <Button
                key={cat.value}
                variant={selectedCategory === cat.value ? "default" : "outline"}
                className={selectedCategory === cat.value 
                  ? "bg-[#D97736] hover:bg-[#F08F4D] text-white" 
                  : "border-[#3D302C] text-[#A89B95] hover:text-[#FDFBF7] hover:border-[#D97736]"
                }
                onClick={() => setSelectedCategory(cat.value)}
                data-testid={`category-${cat.value}`}
              >
                <Icon size={16} className="mr-2" />
                {cat.label}
              </Button>
            );
          })}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {filteredProducts.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card 
                className="bg-[#2C221F] border-[#3D302C] overflow-hidden group hover:border-[#D97736] transition-all duration-300"
                data-testid={`product-card-${index}`}
              >
                <div className="aspect-square overflow-hidden relative">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <Badge className="absolute top-4 right-4 bg-[#D97736] text-white">
                    {product.category}
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-[#FDFBF7] font-['Playfair_Display'] text-xl font-bold mb-2">
                    {product.name}
                  </h3>
                  <p className="text-[#A89B95] text-sm mb-3">{product.description}</p>
                  <div className="mb-4">
                    <span className="text-[#A89B95] text-xs">Available in: </span>
                    <span className="text-[#FDFBF7] text-xs">{product.colors.join(', ')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#CFB53B] text-2xl font-bold font-['Playfair_Display']">
                      ${product.price.toFixed(2)}
                    </span>
                    <Button
                      onClick={() => addToCart(product)}
                      className="bg-[#D97736] hover:bg-[#F08F4D] text-white rounded-full"
                      data-testid={`add-to-cart-${index}`}
                    >
                      <ShoppingCart size={16} className="mr-2" />
                      Add
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
